import React, { useState } from 'react'
import styles from './Gt2Tabs.module.css'

// "Specs, Box Contents & Options" tabbed section, modelled on the Sim-Lab P1X
// layout. Takes the portable-text blocks that used to live behind the buy box's
// "Continue reading" collapsible and splits them into tabs:
//   Specifications - every spec group except "What's Included"
//   In the box     - the "What's Included" group
//   Optional       - the add-ons actually offered in the configurator
// Grouping is by `heading-6` blocks, matching how the description is authored.

const blockText = (b) => (b.children || []).map((c) => c.text || '').join('')

// Group a flat portable-text array into [{ heading, items[], paragraphs[] }].
export function groupByHeading(blocks) {
  const groups = []
  let cur = null
  for (const b of blocks || []) {
    if (b.style === 'heading-6') {
      cur = { heading: blockText(b), items: [], paragraphs: [] }
      groups.push(cur)
      continue
    }
    if (!cur) {
      cur = { heading: null, items: [], paragraphs: [] }
      groups.push(cur)
    }
    const t = blockText(b).trim()
    if (!t) continue
    if (b.listItem === 'bullet') cur.items.push(t)
    else cur.paragraphs.push(t)
  }
  return groups.filter((g) => g.items.length || g.paragraphs.length)
}

const IN_THE_BOX = /what.?s included|in the box/i

function GroupList({ groups }) {
  if (!groups.length) return null
  return (
    <div className={styles.groups}>
      {groups.map((g) => (
        <div className={styles.group} key={g.heading || 'g'}>
          {g.heading && <h4 className={styles.groupTitle}>{g.heading}</h4>}
          {g.items.length > 0 && (
            <ul className={styles.specList}>
              {g.items.map((it) => {
                const [label, ...rest] = it.split(':')
                const value = rest.join(':').trim()
                return (
                  <li className={styles.specRow} key={it}>
                    {value ? (
                      <>
                        <span className={styles.specLabel}>{label.trim()}</span>
                        <span className={styles.specValue}>{value}</span>
                      </>
                    ) : (
                      <span className={styles.specValue}>{it}</span>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          {g.paragraphs.map((p) => (
            <p className={styles.specPara} key={p}>{p}</p>
          ))}
        </div>
      ))}
    </div>
  )
}

function Gt2SpecsTabs({ blocks, optionalItems }) {
  const [tab, setTab] = useState('specs')
  const groups = groupByHeading(blocks)
  if (!groups.length && !(optionalItems || []).length) return null

  const boxGroups = groups.filter((g) => g.heading && IN_THE_BOX.test(g.heading))
  const specGroups = groups.filter((g) => !(g.heading && IN_THE_BOX.test(g.heading)))
  const optional = optionalItems || []

  const TABS = [
    { id: 'specs', label: 'Specifications', show: specGroups.length > 0 },
    { id: 'box', label: 'In the box', show: boxGroups.length > 0 },
    { id: 'optional', label: 'Optional', show: optional.length > 0 },
  ].filter((t) => t.show)

  const active = TABS.some((t) => t.id === tab) ? tab : (TABS[0] && TABS[0].id)

  return (
    <section className={styles.wrap} aria-label="GT-2 specifications, box contents and options">
      <div className={styles.inner}>
        <h2 className={styles.heading}>GT-2 Specs, Box Contents &amp; Options</h2>
        <div className={styles.tabs} role="tablist">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.id}
              role="tab"
              aria-selected={active === t.id}
              className={[styles.tab, active === t.id && styles.tabActive].filter(Boolean).join(' ')}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className={styles.panel} role="tabpanel">
          {active === 'specs' && <GroupList groups={specGroups}/>}
          {active === 'box' && <GroupList groups={boxGroups}/>}
          {active === 'optional' && (
            <ul className={styles.checkList}>
              {optional.map((name) => (
                <li className={styles.checkItem} key={name}><Tick/><span>{name}</span></li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}

export function Tick() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.tick}>
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  )
}

export default Gt2SpecsTabs
